import React, { useEffect, useState } from 'react';
import { LayoutDashboard, Receipt, Key, Users, Settings, Bot, MoreVertical, Plus, X, AlertTriangle, CheckCircle, Info, MousePointerClick, TrendingUp, ShieldAlert, DollarSign, Activity, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Agent {
  name: string;
  status: string;
  outputType: string;
  description: string;
  created: string;
}
interface AssessmentItem {
  label: string;
  value: string;
  source?: string;
}
interface AgentDetail {
  dataSources: string[];
  prompt: string;
  score: number;
  riskLevel: 'Low' | 'Moderate' | 'High';
  summary: string;
  assessments: AssessmentItem[];
  flags: string[];
  recommendation: string;
}
interface ARData {
  outstanding: string;
  current: number;
  days30: number;
  days60: number;
  days90: number;
  terms: string;
  limit: string;
  utilization: number;
}
interface SuggestedAction {
  type: string;
  color: 'green' | 'yellow' | 'red' | 'blue';
  rationale: string;
  buttonText: string;
}
const SCOUT_AVATAR = 'https://cdn.magicpatterns.com/uploads/8MjRsVGxcj4oRZi52oqCxc/image.png';
const scoutInsights: Record<string, string> = {
  // Building Materials
  'Permit & Project Activity': 'Good news — 3 new permits just came through in Collin County totaling $2.1M, up 40% from last quarter. Their pipeline is heating up, which likely means bigger orders ahead. Worth considering a proactive credit line bump so you stay ahead of the ask.',
  'County-Level Legal Filings': "Worth flagging: 2 mechanic's liens totaling $73K showed up in the last 90 days. That typically points to slow payments to subs — and it can trickle uphill. I'd suggest requiring lien waivers on orders over $25K until these clear.",
  'PE Ownership & Rollup Risk': 'Interesting one — they were acquired by a PE platform in 2024 and have already bolted on 4 companies. Leverage is sitting at 4.2x EBITDA, which is on the higher side. With all that integration activity, AP contacts and processes may be shifting. Good idea to reconfirm your terms with the new team.',
  'Consumer & People News': "Something to be aware of: the owner, Rick Dalton, has a separate LLC that filed Chapter 11 in January. There's also been some local press about delayed sub payments. Not a dealbreaker on its own, but it's a signal. Might be worth escalating to senior review and thinking about a personal guarantee.",
  // Manufacturing
  'Supply Chain & Vendor Risk': "They're sourcing 65% of raw materials from just 2 overseas suppliers, and steel is up 12% YoY. That concentration is a risk if either supplier hits a snag. Before extending more credit, it'd be smart to ask about their backup supplier plan.",
  'Environmental & Compliance Filings': "All clear here — no EPA violations in 3 years, one minor OSHA citation that's already resolved, and all permits are current. They're running a clean operation. No changes needed on your end.",
  'Workforce & Labor Risk': "This one needs attention — their union contract expires in 30 days and there's a strike authorization vote on the table. If production stops, payment capacity takes a hit. I'd suggest tightening terms to Net 15 until negotiations wrap up.",
  'Equipment & CapEx Activity': "They just financed $2M in new CNC equipment — that's a solid investment signal. No signs of asset liquidation either. Current credit terms look well-supported by their CapEx trajectory.",
  // Distribution
  'Fleet & Logistics Risk': 'Their fleet is getting up there — 7.2 years average age with 3 recent DOT maintenance flags. Not critical yet, but fleet replacement could hit their cash flow soon. Worth keeping an eye on quarterly.',
  'Warehouse & Inventory Exposure': 'They just locked in a 10-year lease on a new 100K sqft facility, and inventory is turning at 8.5x (well above the 6.2x industry average). These are strong expansion signals — standard terms look appropriate.',
  'Customer Concentration Risk': "Here's one to watch closely: 40% of their revenue comes from a single big-box retailer, and that contract is up for renewal in 6 months. The retailer's own DBT is running at 45 days too. Credit insurance on this account would be a smart move.",
  'Regulatory & Licensing Compliance': 'Everything checks out — active licenses across 14 states, DOT authority is current, no pending actions. Clean slate on the regulatory front.',
  // Food & Beverage
  'FDA & Health Inspection Risk': "This needs your attention — FDA issued a warning letter in January for sanitation issues at their main plant, and there was a Class II recall last year. I'd hold off on any credit limit increases until they can show FDA clearance documentation.",
  'Supply Chain & Commodity Exposure': "Cocoa is up 40% YoY and it's squeezing their margins — they haven't passed the cost through to consumers yet. Keep an eye on their payment patterns over the next couple quarters; cash flow pressure could build.",
  'Brand & Social Sentiment': 'Consumers are fans — 4.4 stars across Yelp and Google, 78% positive social sentiment, and no food safety red flags in public channels. Brand loyalty gives them some pricing power. Low risk here.',
  'Franchise & Location Risk': "They're closing 3 underperforming Midwest hubs (regional sales down 15%) and breaking 2 leases early. Smart cost-cutting, but make sure your shipping addresses are updated and credit limits reflect the smaller footprint.",
  // Software (SaaS)
  'Churn & Revenue Retention': 'Net revenue retention dipped to 98% (was 110%), and product reviews are showing pushback on recent price hikes. CS hiring is also down 40%. If churn keeps ticking up, consider switching them to monthly billing to limit your exposure.',
  'Funding & Burn Rate Risk': "This one's concerning — last raise was Series B in October 2023, runway looks under 6 months, engineering hiring is frozen, and they did a 15% RIF in January. I'd strongly recommend moving them to prepay or credit card terms until their funding picture improves.",
  'Key Person & Talent Risk': 'Founding team is solid — CEO and CTO both 5+ years in, employee reviews show a 4.6/5.0 rating with 92% CEO approval, and engineering tenure averages 3.2 years. This is a stable team. No concerns here.',
  'Product & Market Sentiment': "They're a category leader with enterprise market share growing 20% YoY and 4.8/5.0 on product review sites. Strong product-market fit and happy customers — this is an account worth growing with.",
  // Transportation & Logistics
  'Carrier Authority & Safety': "Their safety score has been sliding — they're now in the 71st percentile, up from 58th (higher is worse). Insurance also renewed 12 days late last quarter, which is a yellow flag. I'd require proof of current insurance with every load and keep a close eye on their next inspection cycle.",
  'Fuel & Operating Cost Exposure': "Diesel is up 18% and they're not hedging at all — fuel is now 34% of their revenue. Their operating ratio is at 96.2%, which is dangerously close to breakeven. If fuel ticks up another 5%, they'll be underwater. Worth tightening to Net 7 so you're not left holding the bag.",
  'Broker & Shipper Payment Behavior': "This one's a real concern — they're factoring 80% of their receivables, there are 2 double-brokering complaints in the last 60 days, and DBT jumped from 8 to 22 days. That combination usually means cash is tight and operations are getting sloppy. I'd put them on hold until you can verify loads directly.",
  'Fleet & Driver Capacity': "Fleet looks decent — 42 trucks averaging 4.8 years old, and they're running at 78% capacity. Driver turnover at 82% is actually below the industry average of 91%, which is a good sign. They're hiring for 6 CDL roles, so they're growing. No immediate concerns, but worth monitoring if capacity drops.",
  // Default agents
  'Financial Risk': "Revenue is down 8% YoY ($12.1M → $11.1M) and net margin compressed from 5.1% to 3.2%. Payments are still okay at 14 DBT, but the trend is softening. I'd hold the credit line where it is and revisit in 6 months once we see if revenue stabilizes.",
  'Operations Risk': "Headcount is steady at ~38, but they're running lean in the warehouse (just 3 staff) and everything runs through one location in Scranton. Not a problem today, but if headcount dips below 35, that's your early warning.",
  'Reputational Risk': 'Looking good — 4.2 stars on Google (47 reviews), only 2 BBB complaints in 24 months (both resolved), and no negative press. Solid reputation. Just keep the standard monitoring in place.',
  'PE Ownership & Sponsor Risk': "They're a subsidiary of Sabre Corp (publicly traded) with no direct PE involvement. Parent leverage is moderate at 2.1x EBITDA. Main thing to track is Sabre's overall health — if the parent wobbles, it flows downhill.",
  'Location/Geopolitical Risk': 'All domestic operations in the Northeast US, clean on OFAC/BIS/EU sanctions, and no meaningful natural disaster exposure. About as low-risk as it gets on the location front.'
};
const defaultAgents: Agent[] = [{
  name: 'Financial Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Assesses financial health and stability via bureau financial statements and payment data',
  created: 'March 19, 2026'
}, {
  name: 'Operations Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Assesses operational stability and execution capability via workforce data',
  created: 'March 19, 2026'
}, {
  name: 'Reputational Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors brand sentiment and customer satisfaction via public reviews and complaints',
  created: 'March 19, 2026'
}, {
  name: 'PE Ownership & Sponsor Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Assesses private equity ownership and leverage risk via revenue insights, ownership data, and financial data',
  created: 'March 19, 2026'
}, {
  name: 'Location/Geopolitical Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Assesses geopolitical exposure and location risk using address, sanctions, and geopolitical data',
  created: 'March 19, 2026'
}];
const buildingMaterialsAgents: Agent[] = [{
  name: 'Permit & Project Activity',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks building permits, project starts, and construction pipeline to assess customer demand and payment capacity',
  created: 'March 19, 2026'
}, {
  name: 'County-Level Legal Filings',
  status: 'Active',
  outputType: 'Structured JSON',
  description: "Monitors mechanic's liens, judgments, and county court filings to detect legal risk and payment disputes",
  created: 'March 19, 2026'
}, {
  name: 'PE Ownership & Rollup Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Identifies PE-backed rollups and acquisition activity that may signal leverage risk or ownership changes',
  created: 'March 19, 2026'
}, {
  name: 'Consumer & People News',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Monitors news about key principals, project owners, and developers behind major accounts',
  created: 'March 19, 2026'
}];
const manufacturingAgents: Agent[] = [{
  name: 'Supply Chain & Vendor Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors supplier concentration, raw material pricing, and vendor financial health',
  created: 'March 19, 2026'
}, {
  name: 'Environmental & Compliance Filings',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks EPA violations, OSHA citations, and environmental permits',
  created: 'March 19, 2026'
}, {
  name: 'Workforce & Labor Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors union activity, labor disputes, turnover, and hiring trends',
  created: 'March 19, 2026'
}, {
  name: 'Equipment & CapEx Activity',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Tracks UCC filings on equipment, capital expenditure signals, and asset condition',
  created: 'March 19, 2026'
}];
const distributionAgents: Agent[] = [{
  name: 'Fleet & Logistics Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors DOT violations, fleet age, fuel cost exposure, and delivery performance',
  created: 'March 19, 2026'
}, {
  name: 'Warehouse & Inventory Exposure',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks warehouse lease obligations, inventory turnover, and storage capacity',
  created: 'March 19, 2026'
}, {
  name: 'Customer Concentration Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Assesses revenue dependency on top customers and contract renewal risk',
  created: 'March 19, 2026'
}, {
  name: 'Regulatory & Licensing Compliance',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Monitors business licenses, permits, and regulatory actions',
  created: 'March 19, 2026'
}];
const foodBeverageAgents: Agent[] = [{
  name: 'FDA & Health Inspection Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks FDA warning letters, health department inspections, and recall history',
  created: 'March 19, 2026'
}, {
  name: 'Supply Chain & Commodity Exposure',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors ingredient cost volatility, supplier stability, and import dependencies',
  created: 'March 19, 2026'
}, {
  name: 'Brand & Social Sentiment',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks consumer reviews, social media sentiment, and food safety mentions',
  created: 'March 19, 2026'
}, {
  name: 'Franchise & Location Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Assesses multi-location operational risk, lease exposure, and regional performance',
  created: 'March 19, 2026'
}];
const softwareAgents: Agent[] = [{
  name: 'Churn & Revenue Retention',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors net revenue retention, customer churn signals, and contract renewals',
  created: 'March 19, 2026'
}, {
  name: 'Funding & Burn Rate Risk',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks funding rounds, runway estimates, and investor sentiment',
  created: 'March 19, 2026'
}, {
  name: 'Key Person & Talent Risk',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Monitors founder/CTO stability, engineering team retention, and employee sentiment',
  created: 'March 19, 2026'
}, {
  name: 'Product & Market Sentiment',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks product reviews, competitive positioning, and market share signals',
  created: 'March 19, 2026'
}];
const transportationAgents: Agent[] = [{
  name: 'Carrier Authority & Safety',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors FMCSA authority status, safety scores, insurance lapses, and operating credentials',
  created: 'March 19, 2026'
}, {
  name: 'Fuel & Operating Cost Exposure',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Tracks diesel price sensitivity, fuel hedging status, and margin compression signals',
  created: 'March 19, 2026'
}, {
  name: 'Broker & Shipper Payment Behavior',
  status: 'Active',
  outputType: 'Structured JSON',
  description: 'Monitors factoring usage, double-brokering red flags, and payment dispute patterns',
  created: 'March 19, 2026'
}, {
  name: 'Fleet & Driver Capacity',
  status: 'Inactive',
  outputType: 'Structured JSON',
  description: 'Tracks driver turnover, equipment age, capacity utilization, and committed load coverage',
  created: 'March 19, 2026'
}];
const industryAgents: Record<string, Agent[]> = {
  'Building Materials': buildingMaterialsAgents,
  Manufacturing: manufacturingAgents,
  Distribution: distributionAgents,
  'Food & Beverage': foodBeverageAgents,
  'Software (SaaS)': softwareAgents,
  'Transportation & Logistics': transportationAgents
};
const defaultAgentDetails: Record<string, AgentDetail> = {
  'Financial Risk': {
    dataSources: ['Bureau Data', 'Revenue Insights', 'Public Filings', 'Bureau Data'],
    prompt: `Analyze the subject company's financial health using financial statements, payment history, and public filings.

Evaluate:
1. Revenue trend (growing, stable, declining)
2. Profit margins and net income trajectory
3. Debt-to-equity ratio and leverage
4. Payment behavior (DBT days, delinquency rate)
5. Cash flow adequacy

Return a risk score (0-100) and structured assessment with key findings and recommended credit limit.`,
    score: 62,
    riskLevel: 'Moderate',
    summary: 'Declining revenue with tightening margins. Payment behavior acceptable but leverage is elevated.',
    assessments: [{
      label: 'Revenue',
      value: 'Down 8% YoY ($12.1M → $11.1M)',
      source: 'Bureau Data'
    }, {
      label: 'Net Margin',
      value: '3.2%, down from 5.1%',
      source: 'Public Filings'
    }, {
      label: 'Leverage',
      value: '1.8x debt-to-equity',
      source: 'Bureau Data'
    }, {
      label: 'Payment Behavior',
      value: 'Avg 14 DBT, 2 late in 12 mo',
      source: 'Bureau Data'
    }, {
      label: 'Credit Limit',
      value: 'Recommended $250,000',
      source: 'Model Output'
    }],
    flags: ['Declining revenue', 'Margin compression'],
    recommendation: 'Approve with enhanced monitoring. Re-evaluate in 6 months.'
  },
  'Operations Risk': {
    dataSources: ['Public Profiles', 'Workforce Insights', 'Job Postings', 'Employee Reviews'],
    prompt: `Assess the subject company's operational stability using workforce data, job postings, and employee reviews.

Evaluate:
1. Headcount trend (growing, stable, shrinking)
2. Key department staffing
3. Leadership stability and tenure
4. Hiring activity vs. layoff signals
5. Employee sentiment and operational risk

Return a risk score (0-100) and structured assessment.`,
    score: 55,
    riskLevel: 'Moderate',
    summary: 'Stable headcount but single-location dependency and lean warehouse operations raise concerns.',
    assessments: [{
      label: 'Headcount',
      value: '~38 employees, flat 12 months',
      source: 'Workforce Insights'
    }, {
      label: 'Sales Team',
      value: '8 reps, fully staffed',
      source: 'Public Profiles'
    }, {
      label: 'Leadership',
      value: 'Regional Mgr 12+ yr tenure',
      source: 'Public Profiles'
    }, {
      label: 'Open Roles',
      value: '1 posting (Jr. Salesman)',
      source: 'Job Postings'
    }, {
      label: 'Employee Sentiment',
      value: '3.4/5.0 — "fun but chaotic"',
      source: 'Employee Reviews'
    }],
    flags: ['Single location dependency', 'Lean warehouse staffing'],
    recommendation: 'Monitor for headcount reductions or leadership changes.'
  },
  'Reputational Risk': {
    dataSources: ['Public Reviews', 'Public Reviews', 'Public Reviews', 'Public Reviews', 'News'],
    prompt: `Monitor the subject company's brand reputation using public reviews, complaint records, and news coverage.

Evaluate:
1. Overall review sentiment (Google, Yelp, Trustpilot)
2. Review volume and recency
3. BBB complaint history and resolution rate
4. Negative press or controversy signals
5. Customer satisfaction trend

Return a risk score (0-100) and structured assessment.`,
    score: 78,
    riskLevel: 'Low',
    summary: 'Strong local reputation with positive reviews. No negative press or unresolved complaints.',
    assessments: [{
      label: 'Google Reviews',
      value: '4.2 ★ (47 reviews)',
      source: 'Public Reviews'
    }, {
      label: 'Yelp',
      value: '3.8 ★ (12 reviews)',
      source: 'Public Reviews'
    }, {
      label: 'Trustpilot',
      value: '4.0 ★ (8 reviews)',
      source: 'Public Reviews'
    }, {
      label: 'BBB Complaints',
      value: '2 in 24 mo, both resolved',
      source: 'Public Reviews'
    }, {
      label: 'News Coverage',
      value: 'No negative press detected',
      source: 'News'
    }],
    flags: [],
    recommendation: 'No action needed — continue monitoring.'
  },
  'PE Ownership & Sponsor Risk': {
    dataSources: ['Ownership Data', 'Public Filings', 'Revenue Insights', 'Funding Data', 'Financial Data'],
    prompt: `Assess whether the subject company has private equity ownership and evaluate associated leverage and sponsor risk.

Evaluate:
1. Ownership structure (PE-backed, founder-owned, public)
2. If PE-backed: sponsor identity, fund vintage, hold period
3. Leverage levels relative to revenue
4. Dividend recapitalization history
5. Exit timeline pressure

Return a risk score (0-100) and structured assessment.`,
    score: 30,
    riskLevel: 'Low',
    summary: 'Subsidiary of publicly traded Sabre Corp. No direct PE involvement or excess leverage.',
    assessments: [{
      label: 'Ownership',
      value: 'Subsidiary of Sabre Corp (public)',
      source: 'Public Filings'
    }, {
      label: 'PE Involvement',
      value: 'None detected',
      source: 'Ownership Data'
    }, {
      label: 'Parent Leverage',
      value: '2.1x EBITDA — moderate',
      source: 'Financial Data'
    }, {
      label: 'Dividend Recap',
      value: 'None detected',
      source: 'Funding Data'
    }],
    flags: ['Parent company dependency'],
    recommendation: 'Monitor parent company (Sabre) financial health.'
  },
  'Location/Geopolitical Risk': {
    dataSources: ['Sanctions Records', 'Disaster Data', 'Census Data', 'Location Data', 'World Bank'],
    prompt: `Assess the subject company's geopolitical exposure and location-based risk using address data, sanctions screening, and regional risk factors.

Evaluate:
1. Primary operating locations
2. Sanctions or restricted party screening
3. Regional economic stability
4. Natural disaster exposure
5. Supply chain geographic concentration

Return a risk score (0-100) and structured assessment.`,
    score: 85,
    riskLevel: 'Low',
    summary: 'Domestic-only operations in stable Northeast US region. Clean sanctions screening.',
    assessments: [{
      label: 'Locations',
      value: 'Scranton, PA (HQ); Nashua, NH',
      source: 'Location Data'
    }, {
      label: 'Sanctions',
      value: 'Clear — OFAC, BIS, EU',
      source: 'Sanctions Records'
    }, {
      label: 'Regional Economy',
      value: 'Stable, low unemployment',
      source: 'Census Data'
    }, {
      label: 'Disaster Risk',
      value: 'Low — no major exposure',
      source: 'Disaster Data'
    }, {
      label: 'Supply Chain',
      value: 'Domestic only, no intl exposure',
      source: 'Internal'
    }],
    flags: [],
    recommendation: 'No action needed.'
  }
};
const buildingMaterialsDetails: Record<string, AgentDetail> = {
  'Permit & Project Activity': {
    dataSources: ['Permit Data', 'Project Data', 'Permit Data', 'Permit Data', 'Location Data'],
    prompt: `Analyze the subject contractor or builder's active permit and project pipeline to assess near-term demand and payment capacity.

Evaluate:
1. Active building permits (count, value, type — residential vs. commercial)
2. Project pipeline trend (growing, stable, declining)
3. Average project size and completion timeline
4. Geographic concentration of projects
5. Permit pull frequency vs. historical average

Return a risk score (0-100) and assessment of whether the customer's project pipeline supports their current credit line.`,
    score: 74,
    riskLevel: 'Low',
    summary: 'Strong permit activity with 12 active projects. Pipeline supports current credit exposure.',
    assessments: [{
      label: 'Active Permits',
      value: '12 permits, $8.4M total value',
      source: 'Permit Data'
    }, {
      label: 'Project Mix',
      value: '8 residential, 4 commercial',
      source: 'Project Data'
    }, {
      label: 'Pipeline Trend',
      value: 'Up 15% vs. prior quarter',
      source: 'Permit Data'
    }, {
      label: 'Avg Project Size',
      value: '$700K, 6-9 mo timeline',
      source: 'Permit Data'
    }, {
      label: 'Geography',
      value: 'Dallas-Fort Worth metro area',
      source: 'Location Data'
    }],
    flags: [],
    recommendation: 'Pipeline is healthy. Current $150K credit line is well-supported.'
  },
  'County-Level Legal Filings': {
    dataSources: ['Court Records', 'Court Records', 'Legal Records', 'State Filings', 'Legal Records'],
    prompt: `Screen the subject contractor for mechanic's liens, judgments, lawsuits, and other county-level legal filings that indicate payment disputes or financial distress.

Evaluate:
1. Mechanic's lien filings (filed by or against the company)
2. Judgment history and amounts
3. Active lawsuits or disputes
4. UCC filings and secured creditors
5. Comparison to industry norms for the region

Return a risk score (0-100) and structured assessment of legal exposure.`,
    score: 48,
    riskLevel: 'Moderate',
    summary: "Two mechanic's liens filed against the company in the last 6 months. One active payment dispute.",
    assessments: [{
      label: "Mechanic's Liens",
      value: '2 filed against in 6 mo ($45K, $28K)',
      source: 'Court Records'
    }, {
      label: 'Judgments',
      value: '1 satisfied judgment ($12K, 2024)',
      source: 'Court Records'
    }, {
      label: 'Active Lawsuits',
      value: '1 — subcontractor payment dispute',
      source: 'Legal Records'
    }, {
      label: 'UCC Filings',
      value: '3 active — equipment financing',
      source: 'State Filings'
    }],
    flags: ["Recent mechanic's liens", 'Active payment dispute'],
    recommendation: 'Place on credit watch. Require lien waivers on orders over $25K.'
  },
  'PE Ownership & Rollup Risk': {
    dataSources: ['Ownership Data', 'Funding Data', 'Public Filings', 'News', 'Financial Data'],
    prompt: `Determine whether the subject company is part of a PE-backed rollup or has recently been acquired. Assess associated leverage and integration risk common in building materials distribution and contracting.

Evaluate:
1. Ownership structure — independent, PE-backed, or part of a platform rollup
2. Recent acquisition or merger activity
3. Sponsor identity and rollup strategy
4. Debt load relative to revenue post-acquisition
5. Integration risk (name changes, leadership turnover, system migrations)

Return a risk score (0-100) and structured assessment.`,
    score: 65,
    riskLevel: 'Moderate',
    summary: 'Acquired by Summit Partners platform in 2024. Elevated leverage post-acquisition with ongoing integration.',
    assessments: [{
      label: 'Ownership',
      value: 'PE-backed — Summit Partners (2024)',
      source: 'Ownership Data'
    }, {
      label: 'Rollup Strategy',
      value: 'Regional roofing consolidation (4 add-ons)',
      source: 'Funding Data'
    }, {
      label: 'Post-Acq Leverage',
      value: '4.2x EBITDA — elevated',
      source: 'Financial Data'
    }, {
      label: 'Integration Status',
      value: 'Rebranding underway, new ERP migration',
      source: 'News'
    }],
    flags: ['High post-acquisition leverage', 'Active integration risk'],
    recommendation: 'Monitor closely. Verify payment terms with new AP contacts post-integration.'
  },
  'Consumer & People News': {
    dataSources: ['News', 'News', 'Court Records', 'Public Profiles', 'Social Media'],
    prompt: `Monitor news and public records related to the key principals, project owners, and developers behind the customer's major projects. Identify reputational or financial risks tied to the people behind the business.

Evaluate:
1. Key principal background (owner, CEO, project leads)
2. Recent news mentions — positive or negative
3. Personal legal issues (bankruptcies, fraud, lawsuits)
4. Developer/project owner financial stability
5. Social media and community reputation

Return a risk score (0-100) and structured assessment.`,
    score: 42,
    riskLevel: 'Moderate',
    summary: 'Owner involved in a separate LLC that filed Chapter 11 in 2025. Local news coverage of delayed project payments.',
    assessments: [{
      label: 'Owner',
      value: 'Rick Dalton — 20 yr industry veteran',
      source: 'Public Profiles'
    }, {
      label: 'Personal Legal',
      value: 'Separate LLC filed Ch. 11 (Jan 2025)',
      source: 'Court Records'
    }, {
      label: 'News Coverage',
      value: '"Dalton Homes delays payments to subs"',
      source: 'News'
    }, {
      label: 'Developer Stability',
      value: 'Primary developer (Apex) financially stable',
      source: 'News'
    }, {
      label: 'Community',
      value: 'Mixed — BBB complaints up 3x YoY',
      source: 'Public Reviews'
    }],
    flags: ['Owner bankruptcy on related entity', 'Negative local press'],
    recommendation: 'Escalate to senior credit review. Consider personal guarantee requirement.'
  }
};
const manufacturingDetails: Record<string, AgentDetail> = {
  'Supply Chain & Vendor Risk': {
    dataSources: ['Bureau Data', 'Supplier Data', 'Commodity Data', 'Bureau Data'],
    prompt: `Analyze supplier concentration, raw material pricing, and vendor financial health.

Evaluate:
1. Supplier concentration (reliance on single-source vendors)
2. Raw material cost trends (steel, aluminum, plastics)
3. Vendor payment behavior and financial health
4. Geographic supply chain exposure

Return a risk score (0-100) and structured assessment.`,
    score: 68,
    riskLevel: 'Moderate',
    summary: 'Heavy reliance on two overseas steel suppliers; raw material costs up 12%.',
    assessments: [{
      label: 'Supplier Concentration',
      value: 'High — 65% from 2 vendors',
      source: 'Bureau Data'
    }, {
      label: 'Raw Material Pricing',
      value: 'Steel +12% YoY',
      source: 'Commodity Data'
    }, {
      label: 'Vendor Health',
      value: 'Primary supplier DBT increased to 22 days',
      source: 'Bureau Data'
    }, {
      label: 'Geo Exposure',
      value: '30% of components from Asia',
      source: 'Supplier Data'
    }],
    flags: ['Supplier concentration', 'Rising material costs'],
    recommendation: 'Request secondary supplier contingency plan.'
  },
  'Environmental & Compliance Filings': {
    dataSources: ['EPA Records', 'OSHA', 'State Environmental Agencies'],
    prompt: `Track EPA violations, OSHA citations, and environmental permits.

Evaluate:
1. Recent OSHA citations or safety incidents
2. EPA violations or fines
3. Status of required environmental permits
4. Historical compliance trends

Return a risk score (0-100) and structured assessment.`,
    score: 85,
    riskLevel: 'Low',
    summary: 'Clean environmental record with no recent OSHA citations.',
    assessments: [{
      label: 'EPA Violations',
      value: '0 violations in 3 yrs',
      source: 'EPA Records'
    }, {
      label: 'OSHA Citations',
      value: '1 minor citation (resolved)',
      source: 'OSHA'
    }, {
      label: 'Permit Status',
      value: 'All active and compliant',
      source: 'State EPA'
    }],
    flags: [],
    recommendation: 'No action needed.'
  },
  'Workforce & Labor Risk': {
    dataSources: ['Labor Records', 'Public Profiles', 'Job Postings', 'Employee Reviews', 'Labor Statistics'],
    prompt: `Monitor union activity, labor disputes, turnover, and hiring trends.

Evaluate:
1. Active union contracts and expiration dates
2. NLRB filings or labor disputes
3. Employee turnover rate vs industry average
4. Hiring velocity for production roles

Return a risk score (0-100) and structured assessment.`,
    score: 40,
    riskLevel: 'High',
    summary: 'Active union contract negotiations with strike authorization vote pending.',
    assessments: [{
      label: 'Union Status',
      value: 'Contract expires in 30 days',
      source: 'Labor Records'
    }, {
      label: 'Labor Disputes',
      value: 'Strike authorization vote pending',
      source: 'Labor Records'
    }, {
      label: 'Turnover',
      value: '18% annual (above average)',
      source: 'Public Profiles'
    }, {
      label: 'Hiring',
      value: 'Frozen for production roles',
      source: 'Job Postings'
    }],
    flags: ['Strike threat', 'Contract expiration'],
    recommendation: 'Shorten credit terms until labor dispute is resolved.'
  },
  'Equipment & CapEx Activity': {
    dataSources: ['State Filings', 'Equipment Data', 'Industry Data'],
    prompt: `Track UCC filings on equipment, capital expenditure signals, and asset condition.

Evaluate:
1. Recent UCC filings for equipment financing
2. CapEx trends vs historical average
3. Secondary market activity (selling off assets)
4. Overall facility modernization signals

Return a risk score (0-100) and structured assessment.`,
    score: 75,
    riskLevel: 'Low',
    summary: 'Recent $2M investment in new CNC machinery; well-capitalized.',
    assessments: [{
      label: 'UCC Filings',
      value: 'New equipment financing (CNC)',
      source: 'State Filings'
    }, {
      label: 'CapEx Trend',
      value: 'Up 25% YoY',
      source: 'Industry Data'
    }, {
      label: 'Asset Sales',
      value: 'None detected',
      source: 'Equipment Data'
    }],
    flags: [],
    recommendation: 'Positive growth signal. Current limits are supported.'
  }
};
const distributionDetails: Record<string, AgentDetail> = {
  'Fleet & Logistics Risk': {
    dataSources: ['DOT Records', 'DOT Records', 'Fuel Data', 'Fleet Data'],
    prompt: `Monitor DOT violations, fleet age, fuel cost exposure, and delivery performance.

Evaluate:
1. DOT safety rating and recent violations
2. Average fleet age and maintenance signals
3. Exposure to fuel price volatility
4. On-time delivery metrics (if public)

Return a risk score (0-100) and structured assessment.`,
    score: 55,
    riskLevel: 'Moderate',
    summary: 'Aging fleet leading to increased maintenance costs and minor DOT violations.',
    assessments: [{
      label: 'DOT Violations',
      value: '3 vehicle maintenance flags',
      source: 'DOT Records'
    }, {
      label: 'Fleet Age',
      value: 'Avg 7.2 years',
      source: 'Fleet Data'
    }, {
      label: 'Fuel Exposure',
      value: 'Unhedged, highly exposed',
      source: 'Fuel Data'
    }, {
      label: 'Safety Rating',
      value: 'Satisfactory',
      source: 'DOT Records'
    }],
    flags: ['Aging fleet', 'Maintenance violations'],
    recommendation: 'Monitor liquidity as CapEx for fleet replacement may be needed soon.'
  },
  'Warehouse & Inventory Exposure': {
    dataSources: ['Real Estate Data', 'Financial Data', 'Industry Data'],
    prompt: `Track warehouse lease obligations, inventory turnover, and storage capacity.

Evaluate:
1. Recent lease signings or facility expansions
2. Inventory turnover ratio vs industry
3. Total square footage and utilization
4. Geographic distribution of warehouses

Return a risk score (0-100) and structured assessment.`,
    score: 82,
    riskLevel: 'Low',
    summary: 'Recently expanded capacity with a new 10-year lease; inventory turning efficiently.',
    assessments: [{
      label: 'Lease Obligations',
      value: 'New 100k sqft facility (10 yr)',
      source: 'Real Estate Data'
    }, {
      label: 'Inventory Turnover',
      value: '8.5x (Industry avg: 6.2x)',
      source: 'Financial Data'
    }, {
      label: 'Footprint',
      value: '3 regional hubs, 250k sqft total',
      source: 'Real Estate Data'
    }],
    flags: [],
    recommendation: 'Strong operational metrics. Approve standard terms.'
  },
  'Customer Concentration Risk': {
    dataSources: ['Bureau Data', 'Bureau Data', 'Public Filings', 'Trade Data'],
    prompt: `Assess revenue dependency on top customers and contract renewal risk.

Evaluate:
1. Percentage of revenue from top 3 customers
2. Financial health of key customers
3. Upcoming contract renewals
4. Customer churn history

Return a risk score (0-100) and structured assessment.`,
    score: 35,
    riskLevel: 'High',
    summary: '40% of revenue tied to a single struggling big-box retailer.',
    assessments: [{
      label: 'Top Customer',
      value: '40% of revenue (Retailer X)',
      source: 'Public Filings'
    }, {
      label: 'Customer Health',
      value: 'Retailer X DBT is 45 days',
      source: 'Bureau Data'
    }, {
      label: 'Renewal Risk',
      value: 'Contract up in 6 mos',
      source: 'Trade Data'
    }],
    flags: ['High customer concentration', 'Key customer distress'],
    recommendation: 'Require credit insurance on the primary customer account.'
  },
  'Regulatory & Licensing Compliance': {
    dataSources: ['Licensing Records', 'FDA Records', 'DOT Records'],
    prompt: `Monitor business licenses, permits, and regulatory actions.

Evaluate:
1. Status of state business and distribution licenses
2. Interstate commerce authority (DOT)
3. Any pending regulatory actions or fines

Return a risk score (0-100) and structured assessment.`,
    score: 90,
    riskLevel: 'Low',
    summary: 'All state and federal licenses are active and in good standing.',
    assessments: [{
      label: 'State Licenses',
      value: 'Active across 14 states',
      source: 'Licensing Records'
    }, {
      label: 'DOT Authority',
      value: 'Active and compliant',
      source: 'DOT Records'
    }, {
      label: 'Regulatory Actions',
      value: 'None detected',
      source: 'Licensing Records'
    }],
    flags: [],
    recommendation: 'No compliance issues detected.'
  }
};
const foodBeverageDetails: Record<string, AgentDetail> = {
  'FDA & Health Inspection Risk': {
    dataSources: ['FDA Records', 'Health Records', 'USDA Records'],
    prompt: `Track FDA warning letters, health department inspections, and recall history.

Evaluate:
1. Recent FDA warning letters or inspections
2. State/local health department scores
3. History of product recalls (Class I, II, III)
4. Import alerts or detentions

Return a risk score (0-100) and structured assessment.`,
    score: 25,
    riskLevel: 'High',
    summary: 'Recent FDA warning letter at primary processing facility regarding sanitation.',
    assessments: [{
      label: 'FDA Status',
      value: 'Warning Letter issued Jan 2026',
      source: 'FDA Records'
    }, {
      label: 'Recalls',
      value: 'Class II recall last year',
      source: 'USDA Records'
    }, {
      label: 'Health Dept',
      value: 'Conditional pass at main plant',
      source: 'Health Records'
    }],
    flags: ['FDA Warning Letter', 'Recent recall'],
    recommendation: 'Suspend credit limit increases until FDA clearance is documented.'
  },
  'Supply Chain & Commodity Exposure': {
    dataSources: ['USDA Records', 'Import Data', 'Bureau Data'],
    prompt: `Monitor ingredient cost volatility, supplier stability, and import dependencies.

Evaluate:
1. Exposure to volatile commodities (cocoa, sugar, wheat)
2. Reliance on imported ingredients
3. Supplier financial health
4. Supply chain disruptions

Return a risk score (0-100) and structured assessment.`,
    score: 60,
    riskLevel: 'Moderate',
    summary: 'Margins pressured by rising cocoa and sugar prices; suppliers stable.',
    assessments: [{
      label: 'Commodity Costs',
      value: 'Cocoa +40% YoY',
      source: 'USDA Records'
    }, {
      label: 'Import Reliance',
      value: '30% from South America',
      source: 'Import Data'
    }, {
      label: 'Supplier Health',
      value: 'Stable, no major DBT shifts',
      source: 'Bureau Data'
    }],
    flags: ['Commodity price exposure'],
    recommendation: 'Monitor payment behavior as margin compression impacts cash flow.'
  },
  'Brand & Social Sentiment': {
    dataSources: ['Public Reviews', 'Public Reviews', 'Social Media', 'Social Media', 'News'],
    prompt: `Track consumer reviews, social media sentiment, and food safety mentions.

Evaluate:
1. Aggregate review scores across platforms
2. Social media sentiment trends
3. Mentions of foodborne illness or quality issues
4. Brand loyalty indicators

Return a risk score (0-100) and structured assessment.`,
    score: 70,
    riskLevel: 'Low',
    summary: 'Strong consumer brand loyalty despite recent price increases.',
    assessments: [{
      label: 'Public Reviews',
      value: '4.4 Avg across locations',
      source: 'Public Reviews'
    }, {
      label: 'Social Sentiment',
      value: '78% Positive',
      source: 'Social Media'
    }, {
      label: 'Safety Mentions',
      value: '0 significant flags',
      source: 'Social Media'
    }],
    flags: [],
    recommendation: 'Brand strength supports continued credit extension.'
  },
  'Franchise & Location Risk': {
    dataSources: ['Real Estate Data', 'Public Reviews', 'State Filings'],
    prompt: `Assess multi-location operational risk, lease exposure, and regional performance.

Evaluate:
1. Net new location growth vs closures
2. Regional sales performance indicators
3. Lease liabilities and real estate footprint
4. Franchisee health (if applicable)

Return a risk score (0-100) and structured assessment.`,
    score: 50,
    riskLevel: 'Moderate',
    summary: 'Closing 3 underperforming regional distribution hubs to cut costs.',
    assessments: [{
      label: 'Location Count',
      value: 'Consolidating from 12 to 9',
      source: 'Real Estate Data'
    }, {
      label: 'Regional Perf.',
      value: 'Midwest sales down 15%',
      source: 'State Filings'
    }, {
      label: 'Lease Exposure',
      value: 'Breaking 2 leases early',
      source: 'Real Estate Data'
    }],
    flags: ['Location closures'],
    recommendation: 'Verify shipping addresses and update account limits based on reduced footprint.'
  }
};
const softwareDetails: Record<string, AgentDetail> = {
  'Churn & Revenue Retention': {
    dataSources: ['Product Reviews', 'Funding Data', 'Public Profiles', 'Job Postings'],
    prompt: `Monitor net revenue retention, customer churn signals, and contract renewals.

Evaluate:
1. Estimated Net Revenue Retention (NRR)
2. Customer sentiment on pricing and renewals
3. Hiring trends in Customer Success
4. Public churn announcements or signals

Return a risk score (0-100) and structured assessment.`,
    score: 45,
    riskLevel: 'Moderate',
    summary: 'Net revenue retention dropped below 100%; mid-market churn increasing.',
    assessments: [{
      label: 'NRR Estimate',
      value: '98% (down from 110%)',
      source: 'Funding Data'
    }, {
      label: 'Churn Signals',
      value: 'Negative reviews on recent price hike',
      source: 'Product Reviews'
    }, {
      label: 'CS Hiring',
      value: 'Down 40% YoY',
      source: 'Public Profiles'
    }],
    flags: ['Declining NRR', 'Pricing backlash'],
    recommendation: 'Monitor closely; switch to monthly billing if possible.'
  },
  'Funding & Burn Rate Risk': {
    dataSources: ['Funding Data', 'Ownership Data', 'Public Filings', 'Funding Data'],
    prompt: `Track funding rounds, runway estimates, and investor sentiment.

Evaluate:
1. Time since last funding round
2. Estimated cash runway and burn rate
3. Investor syndicate strength
4. Cost-cutting signals (layoffs, hiring freezes)

Return a risk score (0-100) and structured assessment.`,
    score: 20,
    riskLevel: 'High',
    summary: 'Last raised in 2023; estimated runway under 6 months with no new term sheet.',
    assessments: [{
      label: 'Last Round',
      value: 'Series B, Oct 2023',
      source: 'Ownership Data'
    }, {
      label: 'Runway Estimate',
      value: '< 6 months',
      source: 'Funding Data'
    }, {
      label: 'Hiring Status',
      value: 'Engineering freeze',
      source: 'Public Profiles'
    }, {
      label: 'Layoffs',
      value: '15% RIF in Jan 2026',
      source: 'News'
    }],
    flags: ['Short runway', 'Hiring freeze', 'Recent layoffs'],
    recommendation: 'Move to prepay or credit card only. High risk of default.'
  },
  'Key Person & Talent Risk': {
    dataSources: ['Public Profiles', 'Employee Reviews', 'Developer Activity', 'News'],
    prompt: `Monitor founder/CTO stability, engineering team retention, and Glassdoor sentiment.

Evaluate:
1. Founder and executive team stability
2. Engineering turnover rate
3. Employee sentiment and CEO approval
4. Key developer activity

Return a risk score (0-100) and structured assessment.`,
    score: 80,
    riskLevel: 'Low',
    summary: 'Core founding team intact; strong engineering retention.',
    assessments: [{
      label: 'Founders',
      value: 'CEO/CTO active (5+ yrs)',
      source: 'Public Profiles'
    }, {
      label: 'Employee Sentiment',
      value: '4.6/5.0, 92% CEO approval',
      source: 'Employee Reviews'
    }, {
      label: 'Eng Retention',
      value: 'High — avg tenure 3.2 yrs',
      source: 'Public Profiles'
    }],
    flags: [],
    recommendation: 'Talent base is stable. No action needed.'
  },
  'Product & Market Sentiment': {
    dataSources: ['Product Reviews', 'Product Reviews', 'Product Reviews', 'Analyst Reports', 'News'],
    prompt: `Track product reviews, competitive positioning, and market share signals.

Evaluate:
1. Category leadership and analyst rankings
2. User review scores and feature sentiment
3. Market share growth vs competitors
4. Product launch velocity

Return a risk score (0-100) and structured assessment.`,
    score: 88,
    riskLevel: 'Low',
    summary: 'Category leader with strong enterprise market share.',
    assessments: [{
      label: 'Analyst Grid',
      value: 'Leader quadrant',
      source: 'Product Reviews'
    }, {
      label: 'Market Share',
      value: 'Growing 20% YoY',
      source: 'Analyst Reports'
    }, {
      label: 'User Reviews',
      value: '4.8/5.0 (Enterprise)',
      source: 'Product Reviews'
    }],
    flags: [],
    recommendation: 'Strong product-market fit supports long-term viability.'
  }
};
const transportationDetails: Record<string, AgentDetail> = {
  'Carrier Authority & Safety': {
    dataSources: ['DOT Records', 'Insurance Records', 'Safety Data', 'Licensing Records'],
    prompt: `Monitor the subject carrier's FMCSA authority, safety scores, and insurance status.

Evaluate:
1. Operating authority status (active, revoked, pending)
2. Safety rating and recent inspection results
3. Insurance coverage status and lapses
4. Out-of-service rates vs. national average

Return a risk score (0-100) and structured assessment.`,
    score: 52,
    riskLevel: 'Moderate',
    summary: 'Active authority but safety score is deteriorating. Insurance renewed late last quarter.',
    assessments: [{
      label: 'Authority',
      value: 'Active — MC and DOT current',
      source: 'DOT Records'
    }, {
      label: 'Safety Score',
      value: '71st percentile (down from 58th)',
      source: 'Safety Data'
    }, {
      label: 'Insurance',
      value: 'Renewed 12 days late last quarter',
      source: 'Insurance Records'
    }, {
      label: 'OOS Rate',
      value: '24% (national avg: 21%)',
      source: 'DOT Records'
    }],
    flags: ['Declining safety score', 'Late insurance renewal'],
    recommendation: 'Require proof of current insurance with each load. Monitor safety score monthly.'
  },
  'Fuel & Operating Cost Exposure': {
    dataSources: ['Fuel Data', 'Commodity Data', 'Financial Data', 'Industry Data'],
    prompt: `Assess the subject carrier's exposure to fuel price volatility and operating cost pressures.

Evaluate:
1. Fuel cost as percentage of revenue
2. Hedging or fuel surcharge strategy
3. Operating ratio trend
4. Rate per mile vs. market benchmarks

Return a risk score (0-100) and structured assessment.`,
    score: 45,
    riskLevel: 'Moderate',
    summary: 'Diesel up 18% YoY with no fuel hedging in place. Operating ratio above 95%.',
    assessments: [{
      label: 'Fuel Costs',
      value: '34% of revenue (up from 28%)',
      source: 'Financial Data'
    }, {
      label: 'Hedging',
      value: 'None — fully exposed',
      source: 'Industry Data'
    }, {
      label: 'Operating Ratio',
      value: '96.2% (breakeven risk)',
      source: 'Financial Data'
    }, {
      label: 'Rate/Mile',
      value: '$2.18 (market avg: $2.35)',
      source: 'Commodity Data'
    }],
    flags: ['No fuel hedging', 'Operating ratio near breakeven'],
    recommendation: 'Tighten terms to Net 7. Monitor weekly for payment slowdowns.'
  },
  'Broker & Shipper Payment Behavior': {
    dataSources: ['Bureau Data', 'Court Records', 'Trade Data', 'News'],
    prompt: `Monitor the subject broker or carrier's payment patterns, factoring usage, and dispute history.

Evaluate:
1. Factoring status and percentage of receivables factored
2. Payment dispute or double-brokering complaints
3. DBT trend and trade payment references
4. Legal filings related to payment disputes

Return a risk score (0-100) and structured assessment.`,
    score: 38,
    riskLevel: 'High',
    summary: 'Factoring 80% of receivables. Two double-brokering complaints filed in the last 60 days.',
    assessments: [{
      label: 'Factoring',
      value: '80% of AR factored',
      source: 'Bureau Data'
    }, {
      label: 'Double-Brokering',
      value: '2 complaints in 60 days',
      source: 'Trade Data'
    }, {
      label: 'DBT Trend',
      value: 'Increased from 8 to 22 days',
      source: 'Bureau Data'
    }, {
      label: 'Legal',
      value: '1 payment dispute filed',
      source: 'Court Records'
    }],
    flags: ['Heavy factoring', 'Double-brokering complaints', 'Rising DBT'],
    recommendation: 'Place on hold. Require proof of delivery and direct carrier verification on all loads.'
  },
  'Fleet & Driver Capacity': {
    dataSources: ['Fleet Data', 'Workforce Insights', 'Job Postings', 'DOT Records'],
    prompt: `Assess the subject carrier's fleet condition, driver retention, and capacity to service committed loads.

Evaluate:
1. Fleet size and average vehicle age
2. Driver turnover rate vs. industry average
3. Active driver hiring and CDL postings
4. Capacity utilization (loads vs. available trucks)

Return a risk score (0-100) and structured assessment.`,
    score: 60,
    riskLevel: 'Moderate',
    summary: 'Fleet is adequate but driver turnover is high. Actively hiring for 6 CDL positions.',
    assessments: [{
      label: 'Fleet Size',
      value: '42 trucks, avg age 4.8 yrs',
      source: 'Fleet Data'
    }, {
      label: 'Driver Turnover',
      value: '82% annual (industry: 91%)',
      source: 'Workforce Insights'
    }, {
      label: 'Open CDL Roles',
      value: '6 active postings',
      source: 'Job Postings'
    }, {
      label: 'Utilization',
      value: '78% of capacity committed',
      source: 'DOT Records'
    }],
    flags: ['High driver turnover'],
    recommendation: 'Capacity is adequate for current volume. Monitor for driver shortages impacting service.'
  }
};
const industryAgentDetails: Record<string, Record<string, AgentDetail>> = {
  'Building Materials': buildingMaterialsDetails,
  Manufacturing: manufacturingDetails,
  Distribution: distributionDetails,
  'Food & Beverage': foodBeverageDetails,
  'Software (SaaS)': softwareDetails,
  'Transportation & Logistics': transportationDetails
};
const arDataByIndustry: Record<string, ARData> = {
  'Building Materials': {
    outstanding: '$142,000',
    current: 60,
    days30: 22,
    days60: 12,
    days90: 6,
    terms: 'Net 30',
    limit: '$150,000',
    utilization: 94
  },
  Manufacturing: {
    outstanding: '$89,000',
    current: 70,
    days30: 18,
    days60: 8,
    days90: 4,
    terms: 'Net 45',
    limit: '$200,000',
    utilization: 44
  },
  Distribution: {
    outstanding: '$215,000',
    current: 55,
    days30: 20,
    days60: 15,
    days90: 10,
    terms: 'Net 30',
    limit: '$250,000',
    utilization: 86
  },
  'Food & Beverage': {
    outstanding: '$178,000',
    current: 65,
    days30: 20,
    days60: 10,
    days90: 5,
    terms: 'Net 30',
    limit: '$200,000',
    utilization: 89
  },
  'Software (SaaS)': {
    outstanding: '$45,000',
    current: 80,
    days30: 15,
    days60: 5,
    days90: 0,
    terms: 'Net 30',
    limit: '$75,000',
    utilization: 60
  },
  'Transportation & Logistics': {
    outstanding: '$187,000',
    current: 50,
    days30: 25,
    days60: 15,
    days90: 10,
    terms: 'Net 15',
    limit: '$200,000',
    utilization: 93
  },
  Other: {
    outstanding: '$98,000',
    current: 72,
    days30: 16,
    days60: 8,
    days90: 4,
    terms: 'Net 30',
    limit: '$150,000',
    utilization: 65
  }
};
const suggestedActions: Record<string, SuggestedAction> = {
  // Building Materials
  'Permit & Project Activity': {
    type: 'Increase Limit',
    color: 'green',
    rationale: 'Pipeline up 40% and AR is healthy at 94% current. Proactively increase from $150K to $200K.',
    buttonText: 'Increase Limit'
  },
  'County-Level Legal Filings': {
    type: 'Place on Hold',
    color: 'red',
    rationale: '$73K in liens + 18% of AR past 60 days. Hold new orders until lien waivers are provided.',
    buttonText: 'Place Hold'
  },
  'PE Ownership & Rollup Risk': {
    type: 'Tighten Terms',
    color: 'yellow',
    rationale: 'Post-acquisition leverage at 4.2x. Move from Net 30 to Net 15 during integration period.',
    buttonText: 'Update Terms'
  },
  'Consumer & People News': {
    type: 'Require Guarantee',
    color: 'yellow',
    rationale: 'Owner Ch.11 on related entity. Require personal guarantee on balances over $50K.',
    buttonText: 'Request Guarantee'
  },
  // Manufacturing
  'Supply Chain & Vendor Risk': {
    type: 'Tighten Terms',
    color: 'yellow',
    rationale: 'Supplier instability + rising costs. Shorten to Net 30 and cap at current limit.',
    buttonText: 'Update Terms'
  },
  'Environmental & Compliance Filings': {
    type: 'Upsell / Grow',
    color: 'green',
    rationale: 'Clean compliance record + steady AR. Good candidate for expanded product lines.',
    buttonText: 'Flag for Sales'
  },
  'Workforce & Labor Risk': {
    type: 'Place on Hold',
    color: 'red',
    rationale: 'Strike risk is imminent. Pause new shipments until labor situation resolves.',
    buttonText: 'Place Hold'
  },
  'Equipment & CapEx Activity': {
    type: 'Increase Limit',
    color: 'green',
    rationale: '$2M CapEx investment signals growth. Consider increasing from $200K to $275K.',
    buttonText: 'Increase Limit'
  },
  // Distribution
  'Fleet & Logistics Risk': {
    type: 'Tighten Terms',
    color: 'yellow',
    rationale: 'Fleet CapEx coming + 25% of AR past 60. Move to Net 15.',
    buttonText: 'Update Terms'
  },
  'Warehouse & Inventory Exposure': {
    type: 'Upsell / Grow',
    color: 'green',
    rationale: 'Expanding capacity + strong inventory turns. Great time to grow the relationship.',
    buttonText: 'Flag for Sales'
  },
  'Customer Concentration Risk': {
    type: 'Get Credit Insurance',
    color: 'blue',
    rationale: '40% revenue concentration + key customer distress. Obtain trade credit insurance immediately.',
    buttonText: 'Request Insurance'
  },
  'Regulatory & Licensing Compliance': {
    type: 'Increase Limit',
    color: 'green',
    rationale: 'Clean regulatory record. Standard limit increase is supported.',
    buttonText: 'Increase Limit'
  },
  // Food & Beverage
  'FDA & Health Inspection Risk': {
    type: 'Place on Hold',
    color: 'red',
    rationale: 'FDA warning letter is active. Suspend limit increases and hold orders over $25K.',
    buttonText: 'Place Hold'
  },
  'Supply Chain & Commodity Exposure': {
    type: 'Tighten Terms',
    color: 'yellow',
    rationale: 'Margin pressure from commodity costs. Shorten to Net 15 until margins stabilize.',
    buttonText: 'Update Terms'
  },
  'Brand & Social Sentiment': {
    type: 'Upsell / Grow',
    color: 'green',
    rationale: 'Strong brand loyalty + healthy AR. Expand product offerings.',
    buttonText: 'Flag for Sales'
  },
  'Franchise & Location Risk': {
    type: 'Reduce Limit',
    color: 'yellow',
    rationale: 'Closing 3 locations reduces their footprint. Adjust limit from $200K to $150K.',
    buttonText: 'Reduce Limit'
  },
  // Software (SaaS)
  'Churn & Revenue Retention': {
    type: 'Tighten Terms',
    color: 'yellow',
    rationale: 'NRR declining + churn signals. Switch to monthly billing.',
    buttonText: 'Update Terms'
  },
  'Funding & Burn Rate Risk': {
    type: 'Move to Cash Only',
    color: 'red',
    rationale: 'Runway under 6 months. Convert to prepay immediately.',
    buttonText: 'Set Cash Only'
  },
  'Key Person & Talent Risk': {
    type: 'Upsell / Grow',
    color: 'green',
    rationale: 'Stable team + low AR risk. Good account to deepen.',
    buttonText: 'Flag for Sales'
  },
  'Product & Market Sentiment': {
    type: 'Increase Limit',
    color: 'green',
    rationale: 'Category leader with growing market share. Increase from $75K to $125K.',
    buttonText: 'Increase Limit'
  },
  // Transportation & Logistics
  'Carrier Authority & Safety': {
    type: 'Tighten Terms',
    color: 'yellow',
    rationale: 'Declining safety score + late insurance renewal. Require proof of insurance per load and move to Net 7.',
    buttonText: 'Update Terms'
  },
  'Fuel & Operating Cost Exposure': {
    type: 'Tighten Terms',
    color: 'yellow',
    rationale: 'Operating ratio at 96% with no fuel hedge. Shorten to Net 7 and cap exposure at current limit.',
    buttonText: 'Update Terms'
  },
  'Broker & Shipper Payment Behavior': {
    type: 'Place on Hold',
    color: 'red',
    rationale: 'Double-brokering complaints + 80% factored AR + rising DBT. Suspend new loads until verified.',
    buttonText: 'Place Hold'
  },
  'Fleet & Driver Capacity': {
    type: 'Monitor',
    color: 'blue',
    rationale: 'Fleet is adequate but driver turnover is a watch item. Set alert for capacity drops below 70%.',
    buttonText: 'Set Alert'
  },
  // Default/Other
  'Financial Risk': {
    type: 'Tighten Terms',
    color: 'yellow',
    rationale: 'Revenue declining + margins compressing. Hold at current limit, re-evaluate in 6 months.',
    buttonText: 'Update Terms'
  },
  'Operations Risk': {
    type: 'Monitor',
    color: 'blue',
    rationale: 'Lean operations but stable. Set automated alert for headcount changes.',
    buttonText: 'Set Alert'
  },
  'Reputational Risk': {
    type: 'Upsell / Grow',
    color: 'green',
    rationale: 'Strong reputation + clean AR. Good candidate for relationship growth.',
    buttonText: 'Flag for Sales'
  },
  'PE Ownership & Sponsor Risk': {
    type: 'Monitor',
    color: 'blue',
    rationale: 'Parent company dependency. Set alert on Sabre Corp financial filings.',
    buttonText: 'Set Alert'
  },
  'Location/Geopolitical Risk': {
    type: 'Increase Limit',
    color: 'green',
    rationale: 'Zero location risk + healthy AR. Standard increase is supported.',
    buttonText: 'Increase Limit'
  }
};
const actionColorStyles = {
  green: {
    bg: 'bg-green-50',
    border: 'border-green-500',
    text: 'text-green-700',
    badge: 'bg-green-100 text-green-800',
    button: 'bg-green-600 hover:bg-green-700 text-white'
  },
  yellow: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-500',
    text: 'text-yellow-800',
    badge: 'bg-yellow-100 text-yellow-800',
    button: 'bg-yellow-600 hover:bg-yellow-700 text-white'
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-500',
    text: 'text-red-800',
    badge: 'bg-red-100 text-red-800',
    button: 'bg-red-600 hover:bg-red-700 text-white'
  },
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-500',
    text: 'text-blue-800',
    badge: 'bg-blue-100 text-blue-800',
    button: 'bg-blue-600 hover:bg-blue-700 text-white'
  }
};
const riskColor = (level: string) => {
  if (level === 'Low') return {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    dot: 'bg-green-500'
  };
  if (level === 'Moderate') return {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    dot: 'bg-yellow-500'
  };
  return {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    dot: 'bg-red-500'
  };
};
const INDUSTRIES = ['Building Materials', 'Manufacturing', 'Distribution', 'Food & Beverage', 'Transportation & Logistics', 'Software (SaaS)', 'Other'] as const;
const PULSE_SCORES: Record<string, {
  score: number;
  level: 'Low' | 'Moderate' | 'High';
}> = {
  'Building Materials': {
    score: 72,
    level: 'Low'
  },
  Manufacturing: {
    score: 64,
    level: 'Moderate'
  },
  Distribution: {
    score: 58,
    level: 'Moderate'
  },
  'Food & Beverage': {
    score: 55,
    level: 'Moderate'
  },
  'Software (SaaS)': {
    score: 48,
    level: 'Moderate'
  },
  'Transportation & Logistics': {
    score: 52,
    level: 'Moderate'
  },
  Other: {
    score: 68,
    level: 'Moderate'
  }
};
const COMPANY_NAMES: Record<string, string> = {
  'Building Materials': 'Dalton Homes Construction',
  Manufacturing: 'Precision Steel Works',
  Distribution: 'Pacific Coast Distributors',
  'Food & Beverage': 'Golden Harvest Foods',
  'Software (SaaS)': 'CloudMetrics Inc.',
  'Transportation & Logistics': 'Apex Freight Solutions'
};
export function AdminDashboard() {
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [showIndustryPicker, setShowIndustryPicker] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [showScout, setShowScout] = useState(false);
  const agents = industryAgents[selectedIndustry] || defaultAgents;
  const agentDetails = industryAgentDetails[selectedIndustry] || defaultAgentDetails;
  const detail = selectedAgent ? agentDetails[selectedAgent] : null;
  const scoutMessage = selectedAgent ? scoutInsights[selectedAgent] : null;
  const currentAR = arDataByIndustry[selectedIndustry] || arDataByIndustry['Other'];
  const suggestedAction = selectedAgent ? suggestedActions[selectedAgent] : null;
  // Auto-open Scout 2 seconds after opening an agent modal
  useEffect(() => {
    if (selectedAgent) {
      setShowScout(false);
      const timer = setTimeout(() => setShowScout(true), 2000);
      return () => clearTimeout(timer);
    } else {
      setShowScout(false);
    }
  }, [selectedAgent]);
  return <div className="p-8 max-w-[1400px] mx-auto w-full" data-id="element-87">
      <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-88">
        Super Admin Dashboard
      </h1>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-89">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto" data-id="element-90">
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-91">
            <LayoutDashboard className="w-4 h-4" data-id="element-92" />
            USAGE DASHBOARD
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-93">
            <Receipt className="w-4 h-4" data-id="element-94" />
            BILLING
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-95">
            <Key className="w-4 h-4" data-id="element-96" />
            API KEYS
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-97">
            <Users className="w-4 h-4" data-id="element-98" />
            GROUPS
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-99">
            <Settings className="w-4 h-4" data-id="element-100" />
            ORGANIZATION SETTINGS
          </button>
          <button className="flex items-center gap-2 px-6 py-4 text-sm font-semibold text-indigo-600 border-b-2 border-indigo-600 whitespace-nowrap" data-id="element-101">
            <Bot className="w-4 h-4" data-id="element-102" />
            AGENTS
          </button>
        </div>

        {/* Content */}
        <div className="p-6" data-id="element-103">
          <div className="flex items-center justify-between mb-6" data-id="element-104">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-105">AI Agents</h2>
            <button className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-106">
              <Plus className="w-4 h-4" data-id="element-107" />
              CREATE AGENT
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-108">
            <table className="w-full text-left text-sm" data-id="element-109">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-110">
                <tr data-id="element-111">
                  <th className="px-6 py-4 font-bold text-gray-900 w-[25%]" data-id="element-112">
                    Name
                  </th>
                  <th className="px-6 py-4 font-bold text-gray-900 w-[12%]" data-id="element-113">
                    Status
                  </th>
                  <th className="px-6 py-4 font-bold text-gray-900 w-[38%]" data-id="element-114">
                    Description
                  </th>
                  <th className="px-6 py-4 font-bold text-gray-900 w-[15%]" data-id="element-115">
                    Created
                  </th>
                  <th className="px-6 py-4 font-bold text-gray-900 w-[5%]" data-id="element-116">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-117">
                {agents.map((agent, idx) => <tr key={idx} className="hover:bg-gray-50 transition-colors" data-id="element-118">
                    <td className="px-6 py-4 align-top" data-id="element-119">
                      <div className="flex items-center gap-2" data-id="element-120">
                        <button onClick={() => setSelectedAgent(agent.name)} className="text-indigo-600 font-medium hover:underline text-left" data-id="element-121">
                          {agent.name}
                        </button>
                        {idx === 0 && !showIndustryPicker && <motion.span initial={{
                      opacity: 0,
                      x: -5
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.5,
                      duration: 0.3
                    }} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-600 whitespace-nowrap" data-id="element-122">
                            <MousePointerClick className="w-3 h-3" data-id="element-123" />
                            click to preview
                          </motion.span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top" data-id="element-124">
                      <div className="flex items-center gap-2" data-id="element-125">
                        {agent.status === 'Active' ? <div className="w-10 h-5 bg-green-600 rounded-full relative flex items-center shrink-0" data-id="element-126">
                            <div className="w-3.5 h-3.5 bg-white rounded-full absolute right-1" data-id="element-127" />
                          </div> : <div className="w-10 h-5 bg-gray-300 rounded-full relative flex items-center shrink-0" data-id="element-128">
                            <div className="w-3.5 h-3.5 bg-white rounded-full absolute left-1" data-id="element-129" />
                          </div>}
                        <span className="text-gray-700" data-id="element-130">{agent.status}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top text-gray-500 leading-relaxed" data-id="element-131">
                      {agent.description}
                    </td>
                    <td className="px-6 py-4 align-top text-gray-700" data-id="element-132">
                      {agent.created}
                    </td>
                    <td className="px-6 py-4 align-top" data-id="element-133">
                      <button className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100" data-id="element-134">
                        <MoreVertical className="w-5 h-5" data-id="element-135" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Industry Picker Modal */}
      <AnimatePresence data-id="element-136">
        {showIndustryPicker && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-137">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-138">
              <div className="px-6 pt-6 pb-2" data-id="element-139">
                <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center mb-4" data-id="element-140">
                  <Bot className="w-6 h-6 text-indigo-600" data-id="element-141" />
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-142">
                  Welcome to AI Agents
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-143">
                  To test this feature, select your industry below and we'll
                  show you the closest use case with real agent configurations
                  and example outputs.
                </p>
              </div>

              <div className="px-6 py-4" data-id="element-144">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-145">
                  Industry
                </label>
                <select value={selectedIndustry} onChange={e => setSelectedIndustry(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none cursor-pointer" data-id="element-146">
                  <option value="" disabled data-id="element-147">
                    Select your industry…
                  </option>
                  {INDUSTRIES.map(industry => <option key={industry} value={industry} data-id="element-148">
                      {industry}
                    </option>)}
                </select>
              </div>

              <div className="px-6 pb-6 pt-2" data-id="element-149">
                <button onClick={() => {
              if (selectedIndustry) setShowIndustryPicker(false);
            }} disabled={!selectedIndustry} className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${selectedIndustry ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-150">
                  Continue
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Agent Detail Modal */}
      <AnimatePresence data-id="element-151">
        {selectedAgent && detail && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedAgent(null)} data-id="element-152">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-153">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-154">
                <div className="flex items-center gap-3" data-id="element-155">
                  <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-156">
                    <Bot className="w-5 h-5 text-indigo-600" data-id="element-157" />
                  </div>
                  <div data-id="element-158">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-159">
                      {selectedAgent}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-160">
                      Agent Configuration & Example Output
                    </p>
                  </div>
                </div>
                <button onClick={() => setSelectedAgent(null)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-161">
                  <X className="w-5 h-5" data-id="element-162" />
                </button>
              </div>

              {/* Modal Body — Two Panels */}
              <div className="flex-1 overflow-auto" data-id="element-163">
                <div className="grid grid-cols-2 divide-x divide-gray-200 min-h-0" data-id="element-164">
                  {/* Left Panel: Configuration */}
                  <div className="p-6 overflow-auto max-h-[70vh]" data-id="element-165">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-166">
                      Configuration
                    </h4>

                    <div className="mb-5" data-id="element-167">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-168">
                        Data Sources
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-169">
                        {[...new Set(detail.dataSources)].map((source, i) => <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200" data-id="element-170">
                            {source}
                          </span>)}
                      </div>
                    </div>

                    <div data-id="element-171">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-172">
                        Prompt
                      </label>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-173">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed" data-id="element-174">
                          {detail.prompt}
                        </pre>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel: Example Output — Readable Card */}
                  <div className="p-6 overflow-auto max-h-[70vh] bg-gray-50" data-id="element-175">
                    <div className="flex items-center justify-between mb-4" data-id="element-176">
                      <div data-id="element-177">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-178">
                          Example Output
                        </h4>
                        <p className="text-sm font-medium text-gray-700" data-id="element-179">
                          {COMPANY_NAMES[selectedIndustry] || 'Dunder Mifflin Paper Company'}
                        </p>
                      </div>
                    </div>

                    {/* Pulse Score + Agent Summary */}
                    {(() => {
                  const pulse = PULSE_SCORES[selectedIndustry] || PULSE_SCORES['Other'];
                  return <div className={`rounded-lg border p-4 mb-4 ${riskColor(pulse.level).bg} ${riskColor(pulse.level).border}`} data-id="element-180">
                          <div className="flex items-center gap-3 mb-3" data-id="element-181">
                            <div className="text-3xl font-bold text-gray-900" data-id="element-182">
                              {pulse.score}
                            </div>
                            <div data-id="element-183">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${riskColor(pulse.level).bg} ${riskColor(pulse.level).text} border ${riskColor(pulse.level).border}`} data-id="element-184">
                                <span className={`w-1.5 h-1.5 rounded-full ${riskColor(pulse.level).dot}`} data-id="element-185" />
                                {pulse.level} Risk
                              </span>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-186">
                                Pulse Score
                              </p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-187">
                            {detail.summary}
                          </p>
                        </div>;
                })()}

                    {/* Assessment Rows */}
                    <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 mb-4" data-id="element-188">
                      {detail.assessments.map((item, i) => <div key={i} className="flex items-start justify-between px-4 py-3" data-id="element-189">
                          <div className="flex-1 min-w-0" data-id="element-190">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide" data-id="element-191">
                              {item.label}
                            </p>
                            <p className="text-sm text-gray-800 mt-0.5" data-id="element-192">
                              {item.value}
                            </p>
                          </div>
                          {item.source && <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100 shrink-0 ml-3 mt-1" data-id="element-193">
                              {item.source}
                            </span>}
                        </div>)}
                    </div>

                    {/* Flags */}
                    {detail.flags.length > 0 && <div className="mb-4" data-id="element-194">
                        <div className="flex items-center gap-1.5 mb-2" data-id="element-195">
                          <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" data-id="element-196" />
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-197">
                            Flags
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2" data-id="element-198">
                          {detail.flags.map((flag, i) => <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200" data-id="element-199">
                              {flag}
                            </span>)}
                        </div>
                      </div>}

                    {/* Recommendation */}
                    <div className="flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg p-3 mb-6" data-id="element-200">
                      <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" data-id="element-201" />
                      <p className="text-sm text-indigo-700" data-id="element-202">
                        {detail.recommendation}
                      </p>
                    </div>

                    {/* AR Snapshot */}
                    <div className="mb-6" data-id="element-203">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-204">
                        AR Snapshot
                      </h4>
                      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-205">
                        <div className="flex items-center justify-between mb-4" data-id="element-206">
                          <div data-id="element-207">
                            <p className="text-xs text-gray-500 font-medium" data-id="element-208">
                              Outstanding Balance
                            </p>
                            <p className="text-xl font-bold text-gray-900" data-id="element-209">
                              {currentAR.outstanding}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-210">
                            <p className="text-xs text-gray-500 font-medium" data-id="element-211">
                              Credit Limit ({currentAR.utilization}% used)
                            </p>
                            <p className="text-sm font-semibold text-gray-700" data-id="element-212">
                              {currentAR.limit}
                            </p>
                          </div>
                        </div>

                        {/* Aging Bar */}
                        <div className="mb-2" data-id="element-213">
                          <div className="h-2 flex rounded-full overflow-hidden bg-gray-100" data-id="element-214">
                            <div style={{
                          width: `${currentAR.current}%`
                        }} className="bg-green-500" data-id="element-215" />
                            <div style={{
                          width: `${currentAR.days30}%`
                        }} className="bg-yellow-400" data-id="element-216" />
                            <div style={{
                          width: `${currentAR.days60}%`
                        }} className="bg-orange-400" data-id="element-217" />
                            <div style={{
                          width: `${currentAR.days90}%`
                        }} className="bg-red-500" data-id="element-218" />
                          </div>
                        </div>

                        <div className="flex justify-between text-[10px] font-semibold text-gray-500" data-id="element-219">
                          <span className="text-green-600" data-id="element-220">
                            {currentAR.current}% Current
                          </span>
                          <span className="text-yellow-600" data-id="element-221">
                            {currentAR.days30}% 1-30
                          </span>
                          <span className="text-orange-500" data-id="element-222">
                            {currentAR.days60}% 31-60
                          </span>
                          <span className="text-red-600" data-id="element-223">
                            {currentAR.days90}% 61-90+
                          </span>
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2" data-id="element-224">
                          <span className="text-xs text-gray-500" data-id="element-225">Terms:</span>
                          <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded" data-id="element-226">
                            {currentAR.terms}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Suggested Action */}
                    {suggestedAction && <div data-id="element-227">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-228">
                          Suggested Action
                        </h4>
                        <div className={`relative overflow-hidden bg-white rounded-lg border ${actionColorStyles[suggestedAction.color].border} shadow-sm`} data-id="element-229">
                          {/* Accent Bar */}
                          <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${actionColorStyles[suggestedAction.color].bg.replace('50', '500')}`} data-id="element-230" />

                          <div className="p-4 pl-5" data-id="element-231">
                            <div className="flex items-start justify-between gap-4" data-id="element-232">
                              <div data-id="element-233">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wide mb-2 ${actionColorStyles[suggestedAction.color].badge}`} data-id="element-234">
                                  {suggestedAction.type}
                                </span>
                                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-235">
                                  {suggestedAction.rationale}
                                </p>
                              </div>
                              <button className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${actionColorStyles[suggestedAction.color].button}`} data-id="element-236">
                                {suggestedAction.buttonText}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Scout Chat Bubble */}
            <AnimatePresence data-id="element-237">
              {showScout && scoutMessage && <motion.div initial={{
            opacity: 0,
            y: 40,
            scale: 0.9
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 20,
            scale: 0.95
          }} transition={{
            type: 'spring',
            damping: 20,
            stiffness: 300
          }} className="fixed bottom-6 right-6 z-[60] w-[380px]" onClick={e => e.stopPropagation()} data-id="element-238">
                  <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" data-id="element-239">
                    {/* Scout Header */}
                    <div className="bg-gradient-to-r from-[#4a7c6f] to-[#3d5a80] px-4 py-3 flex items-center gap-3" data-id="element-240">
                      <img src={SCOUT_AVATAR} alt="Scout" className="w-8 h-8 rounded-full border-2 border-white/30" data-id="element-241" />
                      <div className="flex-1" data-id="element-242">
                        <p className="text-white text-sm font-semibold" data-id="element-243">
                          Scout
                        </p>
                        <p className="text-white/70 text-[10px]" data-id="element-244">
                          Your Credit Copilot
                        </p>
                      </div>
                      <button onClick={() => setShowScout(false)} className="text-white/60 hover:text-white transition-colors" data-id="element-245">
                        <X className="w-4 h-4" data-id="element-246" />
                      </button>
                    </div>

                    {/* Scout Message */}
                    <div className="p-4 max-h-[250px] overflow-auto" data-id="element-247">
                      <div className="flex gap-3" data-id="element-248">
                        <img src={SCOUT_AVATAR} alt="Scout" className="w-7 h-7 rounded-full shrink-0 mt-0.5" data-id="element-249" />
                        <div className="bg-gray-50 rounded-xl rounded-tl-sm px-3.5 py-2.5 border border-gray-100" data-id="element-250">
                          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-251">
                            {scoutMessage}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Scout Input */}
                    <div className="px-4 pb-3" data-id="element-252">
                      <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-200" data-id="element-253">
                        <input type="text" placeholder="Ask Scout a follow-up…" className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none" data-id="element-254" />
                        <button className="text-indigo-500 hover:text-indigo-600 transition-colors" data-id="element-255">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-256">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" data-id="element-257" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>
    </div>;
}